import type { MarketMover, MarketMovers } from '../../types/finance'

// Presentational: the page fetches movers once (getMarketMovers) and shares
// the result with the trending-news card, which seeds off the same symbols.

interface Props {
  movers: MarketMovers | null
  loading: boolean
  onSelect: (symbol: string) => void
}

export default function MarketMoversCard({ movers, loading, onSelect }: Props) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <h2 className="text-slate-900 dark:text-white font-semibold text-sm">Largest movers today</h2>
        {movers && (
          <span className="text-slate-500 text-xs">
            {movers.source === 'mock' ? 'Sample data' : movers.universe}
          </span>
        )}
      </div>

      {loading ? (
        <div className="p-5 animate-pulse space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-8 bg-slate-200 dark:bg-slate-700 rounded" />
          ))}
        </div>
      ) : !movers ? (
        <p className="px-5 py-6 text-center text-slate-500 dark:text-slate-400 text-sm">Movers unavailable.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-200/70 dark:divide-slate-700/50">
          <MoverColumn title="Top gainers" rows={movers.gainers} onSelect={onSelect} />
          <MoverColumn title="Top losers" rows={movers.losers} onSelect={onSelect} />
        </div>
      )}
    </div>
  )
}

function MoverColumn({
  title,
  rows,
  onSelect,
}: {
  title: string
  rows: MarketMover[]
  onSelect: (symbol: string) => void
}) {
  return (
    <div className="py-2">
      <p className="px-5 pt-1 pb-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium">{title}</p>
      {rows.map((m) => {
        const positive = m.changePercent >= 0
        return (
          <button
            key={m.symbol}
            onClick={() => onSelect(m.symbol)}
            className="w-full flex items-center justify-between gap-3 px-5 py-2 hover:bg-slate-200/30 dark:hover:bg-slate-700/30 transition-colors text-left"
          >
            <span className="min-w-0">
              <span className="text-slate-900 dark:text-white font-semibold text-sm">{m.symbol}</span>
              <span className="block text-slate-500 text-xs truncate">{m.name}</span>
            </span>
            <span className="text-right shrink-0">
              <span className="block text-slate-900 dark:text-white text-sm font-medium">${m.price.toFixed(2)}</span>
              <span
                className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold ${
                  positive
                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                    : 'bg-red-500/10 text-red-700 dark:text-red-400'
                }`}
              >
                {positive ? '+' : ''}
                {m.changePercent.toFixed(2)}%
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}
